import { useEffect, useRef, useState } from "react";
import { forceLogin } from "../api/client";

/** Lightweight EventSource hook with auto-reconnect via caller restart. */
export function useEventSource(
  url: string | null,
  eventName: string,
  onData: (payload: string) => void,
) {
  const [live, setLive] = useState(false);
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  useEffect(() => {
    if (!url) {
      setLive(false);
      return;
    }
    const es = new EventSource(url);
    const onEvent = (ev: MessageEvent) => {
      setLive(true);
      onDataRef.current(ev.data);
    };
    const onStreamError = (ev: MessageEvent) => {
      try {
        const data = JSON.parse(String(ev.data || "{}")) as { detail?: unknown };
        if (
          data.detail === "Invalid token"
          || data.detail === "Not authenticated"
          || data.detail === "User inactive"
        ) {
          forceLogin();
        }
      } catch {
        /* ignore malformed stream errors */
      }
    };
    es.addEventListener(eventName, onEvent as EventListener);
    es.addEventListener("stream_error", onStreamError as EventListener);
    es.addEventListener("ping", () => setLive(true));
    es.addEventListener("tick", () => setLive(true));
    es.onopen = () => setLive(true);
    es.onerror = () => setLive(false);
    return () => {
      es.removeEventListener(eventName, onEvent as EventListener);
      es.removeEventListener("stream_error", onStreamError as EventListener);
      es.close();
      setLive(false);
    };
  }, [url, eventName]);

  return live;
}
